import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { useMemo } from "react";
import { never } from "client_legacy/ndaw/errors";
import { isAndroid, isIos } from "client_legacy/ndaw/mobile";
import { navigateTo } from "client_legacy/ndaw/navigator";

export type CommunicationActions = {
	sendEmail?(): void;
	makePhoneCall?(): void;
	startTeamsChat?(): void;
	startTeamsVideoCall?(): void;
};

export type UseCommunicationActionsArgs = {
	emailAddress?: string | null;
	phoneNumber?: string | null;
};

export function useCommunicationActions(args: UseCommunicationActionsArgs): CommunicationActions {
	const {
		emailAddress,
		phoneNumber
	} = args;

	const sendEmail = useMemo(
		(): (() => void) | undefined => {
			if (!emailAddress) {
				return undefined;
			}

			const emailClient: EmailClient = getEffectiveEmailClient();
			if (!emailClient.supportsEmailCompose) {
				return undefined;
			}

			return (): void => {
				emailClient.composeEmail({
					to: emailAddress
				});
			};
		},
		[emailAddress]
	);

	const makePhoneCall = useMemo(
		(): (() => void) | undefined => {
			if (!phoneNumber) {
				return undefined;
			}

			return (): void => {
				navigateTo(`tel:${phoneNumber}`);
			};
		},
		[phoneNumber]
	);

	const communicateViaTeams = useMemo(
		(): ((type: TeamsChatType) => void) | undefined => {
			if (!emailAddress) {
				return undefined;
			}

			return (type: TeamsChatType): void => {
				const teamsUrl: string = "https://teams.microsoft.com";
				let chatUrl: string;

				switch(type){
					case TeamsChatType.Chat:
						chatUrl = `${teamsUrl}/l/chat/0/0?users=${emailAddress}`;
						break;
					case TeamsChatType.Video:
						chatUrl = `${teamsUrl}/l/call/0/0?users=${emailAddress}&withVideo=true`;
						break;
					default:
						never(type);
				}

				navigateTo(chatUrl, true);
			};
		},
		[emailAddress]
	);

	const startTeamsChat = useMemo(
		(): (() => void) | undefined => {
			if (!communicateViaTeams) {
				return undefined;
			}

			return (): void => {
				communicateViaTeams(TeamsChatType.Chat);
			};
		},
		[communicateViaTeams]
	);

	const startTeamsVideoCall = useMemo(
		(): (() => void) | undefined => {
			if (!communicateViaTeams) {
				return undefined;
			}

			const isVideoCallSupported: boolean = !isIos() && !isAndroid();
			if (!isVideoCallSupported) {
				return undefined;
			}

			return (): void => {
				communicateViaTeams(TeamsChatType.Video);
			};
		},
		[communicateViaTeams]
	);

	return useMemo(
		(): CommunicationActions => ({
			sendEmail,
			makePhoneCall,
			startTeamsChat,
			startTeamsVideoCall
		}),
		[
			makePhoneCall,
			sendEmail,
			startTeamsChat,
			startTeamsVideoCall
		]
	);
};

enum TeamsChatType {
	Chat = 1,
	Video = 2
};